import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'

import { ImageLinkProps, TextLinkProps } from '../A'
import ActionButton, { ActionButtonProps } from '../ActionButton'
import { NavMenuProps } from '../NavMenu'
import { colors, sizes } from '../../theme'
import { HeaderSearchProps } from '../HeaderSearch'

import * as SC from './styled'

export type HeaderProps = {
  className?: string
  logos?: ImageLinkProps[]
  logoMain?: ImageLinkProps
  socials?: ActionButtonProps[]
  backgroundColor?: string
  textSearch?: string
  textClose?: string
  textBack?: string
  linkAccount?: TextLinkProps
  linkHome?: TextLinkProps
  textOpenMobileMenu?: string
  intMobileThreshold?: number
  navMenuProps?: NavMenuProps
  search: HeaderSearchProps
  mobileSearchText: string
}

const Header: FC<HeaderProps> = ({
  className,
  logos,
  logoMain,
  socials,
  backgroundColor,
  textSearch,
  textClose,
  textBack,
  linkAccount,
  linkHome,
  textOpenMobileMenu,
  intMobileThreshold = 0,
  navMenuProps,
  search,
  mobileSearchText,
}) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [isDesktopMenuOpen, setDesktopMenuOpen] = useState(false)
  const [openLevelOne, setOpenLevelOne] = useState(-1)
  const containerRef = useRef<HTMLElement>()
  const menuRef = useRef<HTMLElement>()
  const searchInputRef = useRef<HTMLInputElement>()
  const [isSearchVisible, setIsSearchVisible] = useState(false)

  const handleSearchDesktop = useCallback(
    (e) => {
      e.preventDefault()
      setIsSearchVisible(!isSearchVisible)
    },
    [isSearchVisible]
  )

  useEffect(() => {
    if (!searchInputRef?.current?.focus || isSearchVisible === false) {
      return
    }
    searchInputRef.current.focus()
  }, [isSearchVisible])

  const getSearchInput = useCallback(
    (input) => (searchInputRef.current = input),
    []
  )

  const toggleMobileMenuHandler = useCallback(() => {
    setMobileMenuOpen((prev) => !prev)
  }, [setMobileMenuOpen])

  const handleHoverLevelOne = useCallback((index) => {
    setDesktopMenuOpen(true)
    setOpenLevelOne(index)
  }, [])

  const handleCloseMega = useCallback(() => {
    setDesktopMenuOpen(false)
    setOpenLevelOne(-1)
  }, [])

  const renderMegaMenu = useMemo(() => {
    return (
      <SC.MegaMenu
        {...navMenuProps}
        levelOne={openLevelOne}
        isOpen={isDesktopMenuOpen}
        onLeaveHandler={handleCloseMega}
        ref={menuRef}
      />
    )
  }, [navMenuProps, openLevelOne, isDesktopMenuOpen, handleCloseMega])

  useEffect(() => {
    const handle = () => {
      const container = containerRef.current
      const menu = menuRef.current
      if (!container) {
        return
      }
      if (window.innerWidth <= sizes.desktop - 1) {
        // mobile
        container.style.top = ''
      } else if (menu) {
        // desktop
        container.style.top = `-${Math.min(80, window.scrollY)}px`
        menu.style.top = `${-Math.min(80, window.scrollY) + 70}px`
      }
    }
    handle()
    window.addEventListener('scroll', handle)
    return () => {
      window.removeEventListener('scroll', handle)
    }
  }, [intMobileThreshold])

  return (
    <>
      <SC.PlaceHolder />
      <SC.HeaderContainer
        className={className}
        backgroundColor={backgroundColor}
        isDesktopMenuOpen={isDesktopMenuOpen}
        ref={containerRef}
      >
        <SC.HeaderWrapper>
          {logos && logos.length >= 1 && (
            <SC.Logos>
              {logos?.map((logo, index) => (
                <SC.Logo
                  key={`logos-${index}`}
                  {...logo.linkProps}
                  width={logo.imageProps.width}
                >
                  <Image {...logo.imageProps} />
                </SC.Logo>
              ))}
            </SC.Logos>
          )}
          <SC.Main>
            {logoMain && (
              <SC.MainLogo>
                <SC.Logo
                  {...logoMain.linkProps}
                  width={logoMain.imageProps.width}
                >
                  <Image {...logoMain.imageProps} />
                </SC.Logo>
              </SC.MainLogo>
            )}
            <SC.Nav>
              <SC.Menu
                {...navMenuProps}
                onHoverHandler={handleHoverLevelOne}
                currentLevelOneOpen={openLevelOne}
              >
                <SC.SearchInput
                  {...search}
                  visible={isSearchVisible}
                  onReady={getSearchInput}
                />
              </SC.Menu>
              <SC.Icons>
                <SC.Search>
                  <ActionButton
                    actionProps={{
                      onClick: (e) => handleSearchDesktop(e),
                      'aria-label': 'Recherche',
                    }}
                    text={textSearch ?? ''}
                    icon="search"
                    variantType="button"
                    variantColorScheme="iconBlack"
                    variantColor={colors.greenApple}
                    variantIcon="only"
                  />
                </SC.Search>
                <SC.Account>
                  <ActionButton
                    actionProps={{ ...linkAccount }}
                    text={linkAccount?.text ?? ''}
                    icon="account"
                    variantType="link"
                    variantColorScheme="iconBlack"
                    variantColor={colors.greenApple}
                    variantIcon="only"
                  />
                </SC.Account>
                {socials && (
                  <li>
                    <SC.Socials>
                      {socials.map((social, index) => (
                        <li key={`socials-${index}`}>
                          <ActionButton {...social} />
                        </li>
                      ))}
                    </SC.Socials>
                  </li>
                )}
              </SC.Icons>
              <SC.Burger>
                <SC.BurgerButton
                  actionProps={{
                    onClick: toggleMobileMenuHandler,
                    'aria-label': 'Menu',
                  }}
                  text={textOpenMobileMenu ?? ''}
                  icon="menuBurger"
                  variantType="button"
                  variantColorScheme="iconBlack"
                  variantColor={colors.greenApple}
                  variantIcon="only"
                />
              </SC.Burger>
            </SC.Nav>
          </SC.Main>
        </SC.HeaderWrapper>
      </SC.HeaderContainer>
      {renderMegaMenu}
      <SC.Filler aria-hidden={true} />
      <SC.MobileMenu
        isOpen={isMobileMenuOpen}
        toggleMobileMenuOpen={toggleMobileMenuHandler}
        search={search}
        mobileSearchText={mobileSearchText}
        textClose={textClose}
        textBack={textBack}
        linkHome={linkHome}
        linkAccount={linkAccount}
        socials={socials}
        {...navMenuProps}
      />
    </>
  )
}

export default Header
